import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router';
import { useMain } from 'src/views/hooks/useMain';
import BillTemplateRenderer from 'src/views/Billings/pdf/BillTemplateRenderer';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import PageContainer from 'src/components/container/PageContainer';
import { Button, Box, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ReportBillGenerator = ({
  billId: propBillId,
  ownerId: propOwnerId,
  billType: propBillType,
  onBack,
  backButtonLabel = 'Back to Staff Details',
}) => {
  const [data, setData] = useState({});
  const [clientData, setClientData] = useState({});
  const [loading, setLoading] = useState(true);
  const [feedbackUrl, setFeedbackUrl] = useState('');
  const [billUrl, setBillUrl] = useState('');

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const billId = propBillId || params?.billId;
  const ownerId = propOwnerId || location.state?.ownerId;
  const billType = propBillType || location.state?.billType;
  const componentRef = useRef();

  const {
    fetchBillById,
    fetchPackageBillById,
    setShowMessage,
    branchData,
    activeBranchId,
    fetchClientByNumber,
    getFeedbackUrl,
    getBillUrl,
  } = useMain();

  const selectedTemplate = branchData?.selectedBillTemplate !== undefined ? branchData.selectedBillTemplate : 1;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/reports');
    }
  };

  const getData = async (id) => {
    try {
      setLoading(true);
      // Case 1: If billType is 'package' or 'membership', directly fetch package bill
      if (billType === 'package' || billType === 'membership') {
        const packageBillRes = await fetchPackageBillById(id, activeBranchId);
        if (packageBillRes?.statusCode === 200) {
          setData({ ...packageBillRes.data });
        } else {
          setShowMessage({ message: 'Package bill not found', messageType: 'error' });
        }
      } else {
        // Case 2: Try regular bill first
        const regularBillRes = await fetchBillById(id, `?branchId=${activeBranchId}`);
        if (regularBillRes?.statusCode === 200) {
          setData({ ...regularBillRes.data });
        } else {
          // Fallback to package bill
          const packageBillRes = await fetchPackageBillById(id, activeBranchId);
          if (packageBillRes?.statusCode === 200) {
            setData({ ...packageBillRes.data });
          } else {
            setShowMessage({ message: 'Bill not found', messageType: 'error' });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching bill in ReportBillGenerator:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch client data
  const getClientData = async (clientNumber) => {
    try {
      const res = await fetchClientByNumber(clientNumber, activeBranchId);
      if (res?.statusCode === 200) {
        setClientData(res.data);
      }
    } catch (error) {
      console.error('Error fetching client:', error);
    }
  };

  // Fetch feedback URL
  const getUrl = async (id) => {
    try {
      const res = await getFeedbackUrl(id, `?branchId=${activeBranchId}`);
      if (res?.statusCode === 200) {
        setFeedbackUrl(res.data || '');
      }
    } catch (error) {
      console.error('Error fetching feedback URL:', error);
    }
  };

  // Fetch bill URL
  const getBillPage = async (id) => {
    try {
      const res = await getBillUrl(id);
      if (res?.statusCode === 200) {
        setBillUrl(res.data || '');
      }
    } catch (error) {
      console.error('Error fetching bill URL:', error);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const totalProducts =
      data?.products?.reduce((total, product) => total + product.price * product.quantity, 0) || 0;
    const totalServices =
      data?.services?.reduce((total, service) => total + service.price * service.quantity, 0) || 0;
    return totalProducts + totalServices;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return `${day}-${month}-${year}`;
  };

  // Format time
  const formatTime = (time) => {
    if (!time) return '';
    let [hours, minutes] = time.split(':').map(Number);
    const postfix = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes.toString().padStart(2, '0')} ${postfix}`;
  };

  useEffect(() => {
    if (billId && activeBranchId) {
      getData(billId);
      getUrl(billId);
      getBillPage(billId);
    }
  }, [billId, activeBranchId]);

  useEffect(() => {
    if (data?.clientNumber && activeBranchId) {
      getClientData(data.clientNumber);
    }
  }, [data?.clientNumber, activeBranchId]);

  // Prepare all data to pass to templates
  const billTemplateData = {
    ...data,
    branchData,
    clientData,
    feedbackUrl,
    billUrl,
    baseTotal: calculateTotals(),
    formattedDate: formatDate(data?.dateOfBilling),
    formattedTime: formatTime(data?.timeOfBilling),
    ownerId,
  };

  return (
    <PageContainer title="Bill Invoice">
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ fontWeight: 600, textTransform: 'none' }}
        >
          {backButtonLabel}
        </Button>
      </Box>

      {loading ? (
        <ShowLoader />
      ) : Object.keys(data).length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No bill record found
          </Typography>
        </Box>
      ) : (
        <div ref={componentRef}>
          <BillTemplateRenderer
            selectedTemplate={selectedTemplate}
            billData={billTemplateData}
            branchData={branchData}
            printRef={componentRef}
          />
        </div>
      )}
    </PageContainer>
  );
};

export default ReportBillGenerator;

